import type {
  Annotation,
  CameraView,
  Category,
  Item,
  Layer,
  User,
} from '@backend/prisma';
import type {
  CameraViewData,
  Comment,
  DashboardItem,
  ItemData,
  ItemPermissions,
  ItemSettings,
  ItemUpdate,
  LayerData,
  PermissionEntry,
  ToggleLikeResponse,
  TrackViewResponse,
  XYZ,
  XYZW,
} from '@stw/types';
import {
  ITEM_PERMISSION_DISTRIBUTION,
  ITEM_PERMISSION_EDIT,
  VIEW_TYPE,
} from '@stw/types';

import { uploadAndUpdateItem, uploadItemAnnotationImage } from '@/api/upload';
import { createModelBlob, downloadFile } from '@/utils/download';
import { stwFetch } from '@/utils/stwFetch';

export type CameraViewResult = CameraView & {
  target: XYZ;
  position: XYZ;
  rotation: XYZW;
  annotation_id?: string;
};

export type AnnotationResult = Annotation & {
  position: XYZ;
  rotation?: XYZW;
  description?: string;
  image_path?: string | File; // Allow both string paths and File objects
  image_url?: string;
  camera_view_id?: string;
};

export type LayerResult = Layer & {
  annotations: AnnotationResult[];
  camera_views: CameraViewResult[];
};

export type ItemResult = Item & {
  model_type: 'glb' | 'ply' | 'ksplat';
  position: XYZ;
  rotation: XYZW;
  offset: XYZ;
  layers: LayerResult[];
  model_url?: string;
  thumbnail_url: string;
  owner: User;
  categories: Category[];
  is_bookmarked: boolean;
  view_count_total: number;
  is_liked?: boolean;
  userpermission?: {
    distribution: ITEM_PERMISSION_DISTRIBUTION;
    edit: ITEM_PERMISSION_EDIT;
  };
  is_copy?: boolean;
  parent_item?: {
    id: string;
    name: string;
    thumbnail_url: string;
    owner_display_name: string;
  };
  comments: Comment[];
  settings?: ItemSettings | null;
};

export const getCategories = async (): Promise<Category[]> => {
  const { categories } = await stwFetch('GET', '/categories');

  return categories;
};

export const createItem = async (
  modelType: Item['model_type'],
  uploadType: 'model' | 'image' | 'video',
  name?: string
): Promise<{
  item_id: Item['id'];
}> => {
  const { item_id } = await stwFetch('POST', '/item', {
    model_type: modelType,
    upload_type: uploadType,
    ...(name && { name }),
  });

  return { item_id };
};

/**
 * Fetches a list of items from the backend.
 *
 * @param {Object} params A dictionary of query parameters.
 * @param {string} [params.userId] The ID of the user to filter items by.
 * @param {string} [params.categoryId] The ID of the category to filter items by.
 * @param {string} [params.modelType] The type of model to filter items by (either "glb" or "ply").
 * @param {boolean} [params.isPublic] Whether to only return public items.
 * @param {string} [params.search] A search query to filter items by.
 * @param {number} [params.page] The page of items to fetch (1-indexed).
 * @param {number} [params.pageSize] The number of items to fetch per page.
 * @param {boolean} [params.isPublishedOnly] Whether to only return public items.
 * @param {boolean} [params.isEmbedEnabled] Whether to filter by items with embed enabled.
 * @param {string} [params.visibility] The visibility status to filter by (public/unlisted/private).
 * @returns {Promise<{items: DashboardItem[], totalItems: number, pageSize: number, totalPages: number}>} A promise that resolves to pagination response with items and metadata.
 */
export const getItems = async (
  params: {
    userId?: User['id'];
    categoryId?: Category['id'];
    modelType?: Item['model_type'];
    isPublic?: boolean; // Legacy parameter for backward compatibility
    search?: string;
    page?: number;
    pageSize?: number;
    isPublishedOnly?: boolean;
    isEmbedEnabled?: boolean;
    visibility?: string;
    showBookmarksOnly?: boolean;
    includeUserUnlisted?: boolean;
  } = {}
): Promise<{
  items: DashboardItem[];
  totalItems: number;
  pageSize: number;
  totalPages: number;
}> => {
  const queryParams = Object.entries(params)
    .filter(([, value]) => value !== undefined)
    .reduce((searchParams, [key, value]) => {
      searchParams.set(key, String(value));
      return searchParams;
    }, new URLSearchParams());

  const response = await stwFetch('GET', `/items?${queryParams.toString()}`);
  return response;
};

/**
 * Polls for the live-updatable fields (status, Meshy progress) of a set of
 * the caller's own items - used to update dashboard cards while an item is
 * still processing, without re-fetching the full item list.
 * @param {string[]} itemIds - Item ids to check for updates.
 * @returns {Promise<{items: ItemUpdate[]}>} A promise resolving to the updated fields for each item found.
 */
export const getItemUpdates = async (
  itemIds: string[]
): Promise<{ items: ItemUpdate[] }> => {
  if (itemIds.length === 0) return { items: [] };

  return stwFetch('GET', `/items/update?ids=${itemIds.join(',')}`);
};

export const getItem = async (id: string): Promise<ItemResult> => {
  const { item } = await stwFetch('GET', `/item/${id}`);
  return item;
};

export interface FreshAnnotationImageUrl {
  id: string;
  image_url: string;
}

/**
 * Re-signs image_url for every annotation on this item, without re-fetching
 * the whole item — used to patch stale signed URLs in place on long-lived
 * sessions (see `useAnnotationImageRefresh`).
 */
export const getItemAnnotationImageUrls = async (
  id: string
): Promise<{ annotations: FreshAnnotationImageUrl[]; expires_at: number }> => {
  return stwFetch('GET', `/item/${id}/annotation-image-urls`);
};

export interface FreshItemThumbnailUrl {
  id: string;
  thumbnail_url: string;
}

/**
 * Re-signs thumbnail_url for a batch of items, without re-fetching each item
 * individually — used to patch stale signed URLs in place on long-lived
 * builder sessions (see `useItemThumbnailRefresh`).
 */
export const getItemThumbnailUrls = async (
  ids: string[]
): Promise<{ items: FreshItemThumbnailUrl[]; expires_at: number }> => {
  if (ids.length === 0) {
    return { items: [], expires_at: Date.now() };
  }
  const query = ids.map(encodeURIComponent).join(',');
  return stwFetch('GET', `/item/thumbnail-urls?ids=${query}`);
};

export const updateItemSettings = async (
  id: Item['id'],
  settings: Partial<ItemSettings>
): Promise<void> => {
  await stwFetch('PATCH', `/item/${id}/settings`, settings);
};

export const updateItem = async (
  id: Item['id'],
  item: Partial<Item> & { category_ids?: Category['id'][] }
): Promise<void> => {
  await stwFetch('PATCH', `/item/${id}`, item);
};

/**
 * Trigger backend to measure and store actual file size after upload
 */
export const measureItemFileSize = async (
  itemId: Item['id']
): Promise<void> => {
  await stwFetch('POST', `/item/${itemId}/file/size`);
};

export const deleteItem = async (id: Item['id']): Promise<void> => {
  await stwFetch('DELETE', `/item/${id}`);
};

export const cloneItem = async (id: Item['id']): Promise<string> => {
  const { item_id } = await stwFetch('POST', `/item/${id}`);

  return item_id;
};

/**
 * Validate a single email for sharing (real-time validation)
 * @param email - Email address to validate
 * @returns Object with validation status and user info if found
 */
export const validateUserEmail = async (
  email: string
): Promise<{
  valid: boolean;
  user?: { id: string; email: string; username: string };
}> => {
  const response = await stwFetch(
    'GET',
    `/user/validate-email?email=${encodeURIComponent(email)}`
  );
  return response;
};

/**
 * Clone an item to the current user's dashboard
 * @param itemId - The ID of the item to clone
 * @returns The new item ID
 */
export const cloneItemToSelf = async (itemId: Item['id']): Promise<string> => {
  const { item_id } = await stwFetch('POST', `/item/${itemId}/clone`);
  return item_id;
};

/**
 * Get all permissions for an item
 * @param itemId - The ID of the item
 * @returns Object with user_permissions, group_permissions, and public_permission
 */
export const getItemPermissions = async (
  itemId: Item['id']
): Promise<ItemPermissions> => {
  const response = await stwFetch('GET', `/item/${itemId}/permissions`);
  return response;
};

/**
 * Set a permission on an item
 * @param itemId - The ID of the item
 * @param permissionObject - The permission to set
 */
export const setItemPermission = async (
  itemId: Item['id'],
  permissionObject: PermissionEntry
): Promise<void> => {
  await stwFetch('POST', `/item/${itemId}/permissions`, permissionObject);
};

/**
 * Remove a permission from an item
 * @param itemId - The ID of the item
 * @param permission - The permission to remove
 */
export const removeItemPermission = async (
  itemId: Item['id'],
  permission: {
    type: 'user' | 'group' | 'public';
    target_id?: string;
  }
): Promise<void> => {
  await stwFetch('DELETE', `/item/${itemId}/permissions`, permission);
};

export const getSupersplatJwt = async (): Promise<string> => {
  const { jwt } = await stwFetch('GET', `/supersplat/jwt`);

  return jwt;
};

/**
 * Toggle the embed status of an item
 * @param itemId - The ID of the item to toggle embed for
 * @returns Updated embed status, timestamp, and quota information
 */
export const toggleItemEmbed = async (
  itemId: Item['id']
): Promise<{
  is_embed_enabled: boolean;
  embed_enabled_at: string | null;
  embed_credit: number;
  embed_quota: number;
}> => {
  const response = await stwFetch('POST', `/item/${itemId}/embed`);

  return response;
};

/**
 * Update item visibility
 * @param itemId - The ID of the item to update
 * @param visibility - The new visibility setting
 * @returns Updated item
 */
export const updateItemVisibility = async (
  itemId: Item['id'],
  visibility: 'public' | 'unlisted' | 'private'
): Promise<{ item: ItemResult }> => {
  const response = await stwFetch('PATCH', `/item/${itemId}/visibility`, {
    visibility,
  });

  return response;
};

/**
 * Check if an item can be viewed/embedded by anyone (public endpoint - no auth required)
 * @param itemId - The ID of the item to check
 * @returns Viewer access status and basic item information
 */
export const getItemEmbedStatus = async (
  itemId: Item['id']
): Promise<{
  is_embed_enabled: boolean;
}> => {
  const response = await stwFetch('GET', `/item/${itemId}/embed`);

  return response;
};

/**
 * Shared utility to prepare model data for 3D viewers
 * Uses centralized chunked download service
 */
export const prepareModelForViewer = async (
  itemData: ItemData,
  onProgress?: (loaded: number, total: number) => void
): Promise<
  ItemData & {
    modelBlobUrl?: string;
    cleanupBlobUrl?: () => void;
  }
> => {
  if (!itemData.model_url) {
    return itemData;
  }

  // Check if this is our streaming endpoint - uses the new direct streaming endpoint
  if (
    itemData.model_url.includes('/v1/item/') &&
    (itemData.model_url.includes('/file') ||
      itemData.model_url.includes('/file/'))
  ) {
    try {
      const arrayBuffer = await downloadFile(itemData.id, onProgress);

      const { blobUrl, cleanup } = createModelBlob(arrayBuffer);

      // All file types now use data URLs - no blob URLs in network tab
      return {
        ...itemData,
        model_url: blobUrl, // This is now a data URL, not a blob URL
        modelBlobUrl: blobUrl,
        cleanupBlobUrl: cleanup,
      };
    } catch (error) {
      throw new Error(
        `Failed to download model: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  } else {
    // For external URLs (like sample models), use them directly
    return itemData;
  }
};

/**
 * Update the watermark setting for an embedded item
 * @param itemId - The ID of the item to update
 * @param watermarkEnabled - Whether to show watermark in embedded viewer
 * @returns Updated watermark status
 */
export const updateItemWatermark = async (
  itemId: Item['id'],
  isWatermarkEnabled: boolean
): Promise<{
  is_watermark_enabled: boolean;
}> => {
  const response = await stwFetch('PATCH', `/item/${itemId}/embed/watermark`, {
    is_watermark_enabled: isWatermarkEnabled,
  });

  return response;
};

/**
 * Download an item's file using chunked streaming and return blob
 * Pure API function - returns data without UI side effects
 */
export const downloadItemFile = async (
  itemId: string,
  onProgress?: (loaded: number, total: number) => void
): Promise<Blob> => {
  const arrayBuffer = await downloadFile(itemId, onProgress);
  return new Blob([arrayBuffer]);
};

/**
 * Toggle like status for an item
 * @param itemId - The item ID to like/unlike
 * @returns Promise resolving to like status and count
 */
export const toggleItemLike = async (
  itemId: string
): Promise<ToggleLikeResponse> => {
  const response = await stwFetch('POST', `/item/${itemId}/like`);
  return response;
};

/**
 * Track a view for an item
 * @param itemId - The item ID being viewed
 * @param viewType - Type of view (VIEW_TYPE.ITEM_PAGE, VIEW_TYPE.EMBED, VIEW_TYPE.VIEWER)
 * @returns Promise resolving to view statistics
 */
export const trackItemView = async (
  itemId: string,
  viewType: VIEW_TYPE
): Promise<TrackViewResponse> => {
  const response = await stwFetch('POST', `/item/${itemId}/view`, {
    view_type: viewType,
  });
  return response;
};

/**
 * Process annotation images by uploading File objects and replacing them with storage paths
 * @param layers - Array of layer data that may contain annotations with File objects
 * @param itemId - The ID of the item
 * @returns Promise that resolves to processed layers with File objects replaced by storage paths
 */
export const processAnnotationImages = async (
  layers: LayerData[],
  itemId: string
): Promise<LayerData[]> => {
  const processedLayers = await Promise.all(
    layers.map(async (layer) => {
      if (layer.annotations && Array.isArray(layer.annotations)) {
        const processedAnnotations = await Promise.all(
          layer.annotations.map(async (annotation) => {
            if (annotation.image_path instanceof File) {
              const storagePath = await uploadItemAnnotationImage(
                itemId,
                annotation.id,
                annotation.image_path
              );
              return {
                ...annotation,
                image_path: storagePath,
              };
            } else if (
              typeof annotation.image_path === 'string' &&
              annotation.image_path.trim() !== ''
            ) {
              return annotation;
            } else {
              return {
                ...annotation,
                image_path: '',
              };
            }
          })
        );

        return {
          ...layer,
          annotations: processedAnnotations,
        };
      }
      return layer;
    })
  );

  return processedLayers;
};

/**
 * Save item data including layers, annotations, and settings
 * @param itemId - The ID of the item to update
 * @param data - Partial item data to save
 * @returns Promise that resolves to boolean indicating if default camera view exists
 */
export const saveItemData = async (
  itemId: string,
  data: Partial<ItemData>
): Promise<{ hasDefaultCameraView: boolean }> => {
  const { settings, layers, ...itemData } = data;

  let processedLayers = layers;
  if (layers && Array.isArray(layers)) {
    processedLayers = await processAnnotationImages(layers, itemId);
  }

  await updateItem(itemId, {
    ...itemData,
    ...(processedLayers && { layers: processedLayers }),
  } as any);

  if (settings) {
    await updateItemSettings(itemId, settings);
  }

  const hasDefaultCameraView = !!(processedLayers || layers)
    ?.find((layer: LayerData) => layer.order === 0)
    ?.camera_views.find((view: CameraViewData) => view.order === 0);

  return { hasDefaultCameraView };
};

/**
 * Save thumbnail image for an item
 * @param itemId - The ID of the item to update thumbnail for
 * @param file - The thumbnail file to upload
 * @returns Promise that resolves to updated item data
 */
export const saveItemThumbnail = async (
  itemId: string,
  file: File
): Promise<ItemResult> => {
  if (!file) {
    throw new Error('Unable to generate thumbnail.');
  }

  await uploadAndUpdateItem(itemId, file, 'thumbnail');

  // Re-fetch to get updated thumbnail_url since dynamic path construction means URLs are available immediately
  const updatedItem = await getItem(itemId);
  if (!updatedItem) {
    throw new Error('Failed to retrieve updated item after thumbnail upload.');
  }

  return updatedItem;
};

/**
 * Create a user comment annotation on layer -1
 * @param itemId - The ID of the item
 * @param data - Annotation data including position, rotation, name, and optional initial comment
 * @returns Promise that resolves to the created annotation
 */
export const createUserAnnotation = async (
  itemId: string,
  data: {
    name: string;
    position: XYZ;
    rotation?: XYZW;
    comment_content?: string;
  }
) => {
  const result = await stwFetch('POST', `/item/${itemId}/annotation`, data);
  return result as { annotation: AnnotationResult; comment: Comment };
};
